import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@monigo/ui';
import { useTransfer } from '../transfers/api.js';
import { supabase } from '../../lib/supabase.js';
import { qk } from '../../lib/api/queryKeys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { useState } from 'react';

interface SettingsRow {
  key: string;
  value_jsonb: Record<string, unknown>;
}

export function PaymentInstructionsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const transferQ = useTransfer(id);
  const [copied, setCopied] = useState<string | null>(null);

  const collectionKey = transferQ.data?.transfer.payment_instructions_jsonb?.settings_key;
  const settingsQ = useQuery({
    queryKey: [...qk.settings, collectionKey],
    queryFn: async (): Promise<SettingsRow | null> => {
      if (!collectionKey) return null;
      const { data } = await supabase
        .from('settings')
        .select('key,value_jsonb')
        .eq('key', collectionKey)
        .maybeSingle();
      return data as SettingsRow | null;
    },
    enabled: !!collectionKey,
  });

  if (transferQ.isLoading) return <LoadingScreen />;
  if (transferQ.isError || !transferQ.data)
    return <ErrorState onRetry={() => transferQ.refetch()} />;

  const transfer = transferQ.data.transfer;
  const account = settingsQ.data?.value_jsonb;

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="space-y-4">
        <Link
          to={`/transfers/${transfer.id}`}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-elevated text-2xl text-text"
        >
          ←
        </Link>
        <div className="space-y-2">
          <h1 className="text-4xl font-semibold text-primary">{t('payment.title')}</h1>
          <p className="text-xl leading-8 text-text-muted">
            Payment or transfer is not automatic. Use the details below to pay manually, then your
            transfer can be processed.
          </p>
        </div>
      </div>

      <div className="space-y-5 border-t border-border pt-6">
        <div className="rounded-[1.5rem] bg-success/35 p-5">
          <p className="text-lg text-text-muted">{t('payment.labelAmount')}</p>
          <p className="amount mt-2 text-4xl font-semibold text-text">
            {transfer.amount_send} {transfer.from_currency}
          </p>
        </div>
        <dl className="flex flex-col gap-4 text-lg">
          <CopyRow
            label={t('payment.reference')}
            value={transfer.reference_code}
            copied={copied === 'ref'}
            onCopy={() => {
              navigator.clipboard.writeText(transfer.reference_code).then(() => {
                setCopied('ref');
                setTimeout(() => setCopied(null), 1500);
              });
            }}
          />
          {account ? (
            Object.entries(account).map(([k, v]) => (
              <CopyRow
                key={k}
                label={k}
                value={String(v ?? '')}
                copied={copied === k}
                onCopy={() => {
                  navigator.clipboard.writeText(String(v ?? '')).then(() => {
                    setCopied(k);
                    setTimeout(() => setCopied(null), 1500);
                  });
                }}
              />
            ))
          ) : (
            <p className="text-lg text-warning">{t('payment.missingSettings')}</p>
          )}
        </dl>
      </div>

      <div className="space-y-4 pt-4">
        <Link to={`/transfers/${transfer.id}`} className="block w-full">
          <Button className="h-16 w-full text-2xl font-medium">{t('payment.viewTransfer')}</Button>
        </Link>
        <Link to="/support" className="block text-center text-2xl font-medium text-primary">
          GET HELP
        </Link>
      </div>
    </div>
  );
}

function CopyRow({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
      <span className="text-text-muted">{label}</span>
      <span className="flex items-center gap-2 text-right">
        <span className="font-mono text-text">{value}</span>
        <button
          type="button"
          onClick={onCopy}
          className="rounded-pill bg-surface-elevated px-3 py-2 text-sm font-medium text-text"
        >
          {copied ? t('common.copied') : t('common.copy')}
        </button>
      </span>
    </div>
  );
}
